import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import Layout from '../components/Layout';
import { FaClipboardList, FaPlus, FaUserCheck, FaUsers, FaUser, FaChartPie, FaTimes, FaCompass } from 'react-icons/fa';
import ProfileTab from '../components/ProfileTab';
import ReportsTab from '../components/ReportsTab';
import ConfirmationModal from '../components/ConfirmationModal';

// Modular Components
import ClubProjectsTable from '../components/dashboard/ClubProjectsTable';
import ClubMembersTable from '../components/dashboard/ClubMembersTable';
import ClubAttendanceTable from '../components/dashboard/ClubAttendanceTable';
import ClubModals from '../components/dashboard/ClubModals';
import DiscoverTab from '../components/dashboard/DiscoverTab';

const ClubAdminDashboard = () => {
    const [user, setUser] = useState(null);
    const [projects, setProjects] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [formData, setFormData] = useState({});
    const [attendanceList, setAttendanceList] = useState([]);
    const [clubAttendance, setClubAttendance] = useState([]);
    const [projectCategories, setProjectCategories] = useState([]);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, id: null });
    const [clubType, setClubType] = useState(''); // 'Alpha' or 'Omega'

    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('projects');

    const tabs = [
        { id: 'projects', label: 'My Projects', icon: <FaClipboardList /> },
        { id: 'discover', label: 'Discover', icon: <FaCompass /> },
        { id: 'members', label: 'Members', icon: <FaUsers /> },
        { id: 'attendance', label: 'Attendance', icon: <FaUserCheck /> },
        { id: 'reports', label: 'Reports', icon: <FaChartPie /> },
        { id: 'profile', label: 'My Profile', icon: <FaUser /> },
    ];

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            navigate('/login');
            return;
        }
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'club_admin') {
            navigate('/login');
            return;
        }
        setUser(userData);
        loadAllData();
    }, [navigate]);

    const loadAllData = async () => {
        setLoading(true);
        await Promise.all([
            fetchProjects(),
            fetchMembers(),
            fetchClubAttendance(),
            fetchProjectCategories()
        ]);
        setLoading(false);
    };

    const fetchProjects = async () => {
        const data = await adminApi.getProjects();
        if (data.success) {
            setProjects(data.data.projects);
            if (data.data.projects.length > 0) {
                setClubType(data.data.projects[0].club_category);
            }
        }
    };

    const fetchMembers = async () => {
        const data = await adminApi.getAdmins();
        if (data.success) setMembers(data.data.admins);
    };

    const fetchAttendance = async (projectId) => {
        const data = await adminApi.getProjectAttendance(projectId);
        if (data.success) setAttendanceList(data.data.attendance);
        else toast.error(data.message);
    };

    const fetchClubAttendance = async () => {
        const data = await adminApi.getAllAttendance();
        if (data.success) setClubAttendance(data.data.attendance);
    };

    const fetchProjectCategories = async () => {
        const data = await adminApi.getCategories();
        if (data.success) setProjectCategories(data.data.categories);
    };

    const openModal = (type, data = {}) => {
        setModalType(type);
        setFormData(type === 'create-project' ? { is_open: 1, ...data } : data);
        setShowModal(true);
        if (type === 'view-attendance') fetchAttendance(data.id);
    };

    const closeModal = () => {
        setShowModal(false);
        setFormData({});
        setAttendanceList([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let data;
            if (modalType === 'create-project') {
                data = await adminApi.createProject({ ...formData, club_id: user.club_id });
            } else if (modalType === 'edit-project') {
                data = await adminApi.updateProject(formData);
            } else if (modalType === 'edit-member') {
                data = await adminApi.updateAdmin(formData);
            } else if (modalType === 'create-member') {
                const autoPassword = formData.admission_number || formData.nic_number;
                if (!autoPassword) throw new Error('Admission or NIC required for password');
                data = await adminApi.createAdmin({ ...formData, role: 'member', password: autoPassword });
            } else if (modalType === 'mark-attendance') {
                data = await adminApi.markAttendance(formData);
            }

            if (data.success) {
                toast.success('Success!');
                closeModal();
                if (modalType === 'create-member' || modalType === 'edit-member') fetchMembers();
                else fetchProjects();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (id, type = 'project') => {
        setConfirmModal({
            isOpen: true,
            type,
            id,
            title: `Delete ${type === 'member' ? 'Member' : 'Project'}`,
            message: `Are you sure you want to delete this ${type}? This action cannot be undone.`
        });
    };

    const executeDelete = async () => {
        const { id, type } = confirmModal;
        const data = type === 'member' ? await adminApi.deleteAdmin(id) : await adminApi.deleteProject(id);
        if (data.success) {
            if (type === 'member') fetchMembers();
            else fetchProjects();
            toast.success(`${type} deleted successfully`);
            setConfirmModal({ isOpen: false, type: null, id: null });
        } else {
            toast.error(data.message);
        }
    };

    return (
        <Layout user={user} activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs}>
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter uppercase">
                        {['profile', 'discover'].includes(activeTab) ? ' ' :
                            (activeTab === 'reports' ? 'Analytics & Reports' :
                                (activeTab === 'projects' ? 'My Projects' :
                                    (activeTab === 'attendance' ? 'Club Attendance' : 'Club Members')))}
                    </h2>
                    <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
                        {!['profile', 'reports', 'attendance', 'discover'].includes(activeTab) && (
                            activeTab === 'projects' ? (
                                <React.Fragment>
                                    <button onClick={() => openModal('mark-attendance')} className="btn-secondary flex items-center gap-2 inline-flex">
                                        <FaUserCheck /> Mark Attendance
                                    </button>
                                    <button onClick={() => openModal('create-project')} className="btn-primary flex items-center gap-2 inline-flex">
                                        <FaPlus /> Create Project
                                    </button>
                                </React.Fragment>
                            ) : (
                                <button onClick={() => openModal('create-member')} className="btn-primary flex items-center gap-2 inline-flex">
                                    <FaPlus /> Add Member
                                </button>
                            )
                        )}
                    </div>
                </div>

                {activeTab === 'profile' ? (
                    <ProfileTab user={user} />
                ) : activeTab === 'discover' ? (
                    <DiscoverTab />
                ) : activeTab === 'reports' ? (
                    <ReportsTab user={user} />
                ) : (
                    <div className="glass-card overflow-hidden overflow-x-auto">
                        {activeTab === 'projects' && (
                            <ClubProjectsTable projects={projects} openModal={openModal} handleDeleteClick={handleDeleteClick} />
                        )}
                        {activeTab === 'members' && (
                            <ClubMembersTable members={members} openModal={openModal} handleDeleteClick={handleDeleteClick} />
                        )}
                        {activeTab === 'attendance' && (
                            <ClubAttendanceTable attendance={clubAttendance} />
                        )}
                    </div>
                )}
            </div>

            {/* Shared Modal Overlay */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/10 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-slate-100">
                        <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white">
                            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                                {modalType.replace('-', ' ')}
                            </h3>
                            <button onClick={closeModal} className="w-10 h-10 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all">
                                <FaTimes size={18} />
                            </button>
                        </div>
                        <div className="p-8">
                            <ClubModals
                                modalType={modalType}
                                formData={formData}
                                setFormData={setFormData}
                                handleSubmit={handleSubmit}
                                closeModal={closeModal}
                                projects={projects}
                                attendanceList={attendanceList}
                                clubType={clubType}
                                projectCategories={projectCategories}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                onConfirm={executeDelete}
                title={confirmModal.title}
                message={confirmModal.message}
            />
        </Layout>
    );
};

export default ClubAdminDashboard;

